import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Ticket, ClipboardList, ScanLine, Menu,
  CalendarDays, Users, UserPlus, Megaphone, BarChart3,
  Palette, Mail, Settings, Compass, LogOut, Map,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { ScareLogo } from "./Brand";
import EventSwitcher from "./EventSwitcher";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "./ui/sheet";

const PRIMARY = [
  { to: "/dashboard/overview", label: "Dashboard", icon: LayoutDashboard, testid: "mnav-dashboard" },
  { to: "/dashboard/tickets", label: "Tickets", icon: Ticket, testid: "mnav-tickets" },
  { to: "/dashboard/orders", label: "Orders", icon: ClipboardList, testid: "mnav-orders" },
  { to: "/dashboard/checkin", label: "Check-In", icon: ScanLine, testid: "mnav-checkin" },
];

const MORE = [
  { to: "/dashboard/open-days", label: "Open Days", icon: CalendarDays, testid: "mnav-open-days" },
  { to: "/dashboard/attendees", label: "Attendees", icon: Users, testid: "mnav-attendees" },
  { to: "/dashboard/guest-lists", label: "Guest Lists", icon: UserPlus, testid: "mnav-guest-lists" },
  { to: "/dashboard/marketing", label: "Marketing", icon: Megaphone, testid: "mnav-marketing" },
  { to: "/dashboard/reports", label: "Reports", icon: BarChart3, testid: "mnav-reports" },
  { to: "/dashboard/reach", label: "Reach", icon: Map, testid: "mnav-reach" },
  { to: "/dashboard/branding", label: "Branding", icon: Palette, testid: "mnav-branding" },
  { to: "/dashboard/emails", label: "Emails", icon: Mail, testid: "mnav-emails" },
  { to: "/dashboard/settings", label: "Settings", icon: Settings, testid: "mnav-settings" },
];

const MORE_PATHS = MORE.map((m) => m.to);

export function MobileTopBar() {
  const nav = useNavigate();
  return (
    <header
      className="lg:hidden sticky top-0 z-40 bg-[#0c0c0c]/95 backdrop-blur border-b border-[#1a1a1a] px-4 py-2.5 flex items-center gap-3"
      style={{ paddingTop: "max(0.625rem, env(safe-area-inset-top))" }}
      data-testid="mobile-topbar"
    >
      <button onClick={() => nav("/dashboard/overview")} className="shrink-0" data-testid="mobile-brand">
        <ScareLogo size={18} withTagline={false} />
      </button>
      <div className="flex-1 min-w-0">
        <EventSwitcher />
      </div>
    </header>
  );
}

export function MobileBottomNav() {
  const [moreOpen, setMoreOpen] = useState(false);
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const moreActive = MORE_PATHS.some((p) => loc.pathname.startsWith(p));

  const tabClass = (active) =>
    `flex flex-col items-center justify-center gap-1 flex-1 py-2 text-[10px] font-medium transition-colors ${
      active ? "text-[#dc2626]" : "text-[#8a8a8a] hover:text-white"
    }`;

  return (
    <>
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0c0c0c]/95 backdrop-blur border-t border-[#1a1a1a] flex items-stretch"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="mobile-bottom-nav"
      >
        {PRIMARY.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink key={n.to} to={n.to} data-testid={n.testid}
                     className={({ isActive }) => tabClass(isActive)}>
              <Icon size={22} />
              <span>{n.label}</span>
            </NavLink>
          );
        })}
        <button onClick={() => setMoreOpen(true)} className={tabClass(moreActive)} data-testid="mnav-more">
          <Menu size={22} />
          <span>More</span>
        </button>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent
          side="bottom"
          className="lg:hidden bg-[#0c0c0c] border-t border-[#1a1a1a] rounded-t-2xl p-0 max-h-[85vh] overflow-y-auto"
          data-testid="mobile-more-sheet"
        >
          <SheetHeader className="px-5 pt-5 pb-3 text-left">
            <SheetTitle className="text-white text-base">Menu</SheetTitle>
          </SheetHeader>

          <div className="grid grid-cols-3 gap-2 px-4 pb-4">
            {MORE.map((n) => {
              const Icon = n.icon;
              const active = loc.pathname.startsWith(n.to);
              return (
                <button
                  key={n.to}
                  onClick={() => { setMoreOpen(false); nav(n.to); }}
                  data-testid={n.testid}
                  className={`flex flex-col items-center justify-center gap-2 rounded-xl border p-3 text-xs font-medium transition-colors ${
                    active
                      ? "border-[#dc2626] bg-[#dc2626]/10 text-[#dc2626]"
                      : "border-[#1a1a1a] bg-[#0e0e0e] text-[#cfcfcf] hover:border-[#2a2a2a] active:bg-[#161616]"
                  }`}
                >
                  <Icon size={20} />
                  <span>{n.label}</span>
                </button>
              );
            })}
          </div>

          <div className="px-4 pb-4">
            <button
              onClick={() => { setMoreOpen(false); nav("/dashboard/open-days?tour=force"); }}
              className="w-full flex items-center gap-2 rounded-xl border border-[#1a1a1a] bg-[#0e0e0e] px-4 py-3 text-sm text-[#cfcfcf] active:bg-[#161616]"
              data-testid="mnav-take-a-tour"
            >
              <Compass size={18} /> Take a tour
            </button>
          </div>

          <div className="border-t border-[#1a1a1a] px-5 py-4 flex items-center justify-between"
               style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate" data-testid="mobile-username">{user?.name || "Admin"}</div>
              <div className="text-xs text-[#8a8a8a] truncate">{user?.email}</div>
            </div>
            <button
              onClick={async () => { setMoreOpen(false); await logout(); nav("/login"); }}
              className="st-btn st-btn-ghost shrink-0"
              data-testid="mobile-logout"
            >
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
